import { call, select, fork, all, takeEvery, put } from 'redux-saga/effects';
import {
  DELIVERYTYPE_SHIPPING,
  DELIVERYTYPE_STOREPICKUP,
  DOCTYPE_ENTITY,
  DOCTYPE_INVENTORY_RETURN,
  DOCTYPE_INVENTORY_TRANSFER,
  DOCTYPE_LOCATION,
  DOCTYPE_ORDER_MANAGEMENT_CONTROL_RECORD,
  DOCTYPE_PURCHASE_ORDER,
  DOCTYPE_SALE_TRANSACTION,
  DOCTYPE_SUBSIDIARY,
} from '@krispsystems/constants';
import set from 'lodash/set';

import {
  email,
  generatePdf,
  getDoc,
  getDocs,
} from '../../services/apiCommon';
import { fetchPrintTemplateById } from '../../services/print_template/api_print_template';

import { EXPORT_EMAIL_TRANSACTION, EXPORT_PRINT_TRANSACTION } from '../../constants/constant_saga_types';
import { IMMEDIATE_DOWNLOAD } from '../../constants/constant_action_types';
import {
  ITEM_TYPE_COUPON,
  ITEM_TYPE_DISCOUNT,
  ITEM_TYPE_GIFTCERTIFICATE,
  ITEM_TYPE_GRATUITY,
  ITEM_TYPE_LOYALTY,
  ITEM_TYPE_PAYMENT_SURCHARGE,
  ITEM_TYPE_PROMOTION,
} from '../../constants/item_types';

import {
  setEmailTransactionModalData,
  setEmailTransactionModalLoading,
  showEmailTransactionModal,
} from '../../components/admin/modals/EmailTransactionModal/EmailTransactionModalActions';

import { getParticularPreferenceSettings } from '../../common/selectors/preference';
import { getCurrentUserId, isUserAdmin } from '../../common/selectors/selector_common';

import Handlebars from '../../utils/handlebar';

const EXPORT_PREFERENCE_MAP = {
  [DOCTYPE_PURCHASE_ORDER]: {
    email_attachment: 'email_attachment_template_purchase_order',
    print: 'print_template_purchase_order',
  },
  [DOCTYPE_INVENTORY_RETURN]: {
    email_attachment: 'email_attachment_template_inventory_return',
    print: 'print_template_inventory_return',
  },
  [DOCTYPE_INVENTORY_TRANSFER]: {
    email_attachment: 'email_attachment_template_inventory_transfer',
    print: 'print_template_inventory_transfer',
  },
  [DOCTYPE_SALE_TRANSACTION]: {
    email_attachment: 'email_attachment_template_sale_admin',
    email: 'email_body_template_sale_admin',
    oms: 'print_template_sale_oms',
    print: 'print_template_sale_admin',
  },
  [DOCTYPE_ORDER_MANAGEMENT_CONTROL_RECORD]: {
    [`label_${DELIVERYTYPE_SHIPPING}`]: 'oms_shipping_label',
    [`label_${DELIVERYTYPE_STOREPICKUP}`]: 'oms_pickup_store_label',
    pack: 'oms_packing_slip',
    pick: 'oms_picking_list',
    pickup: 'oms_pickup_label',
  },
};

const DEPENDENCY_PATH_DOCTYPE_MAP = {
  customer: DOCTYPE_ENTITY,
  location: DOCTYPE_LOCATION,
  subsidiary: DOCTYPE_SUBSIDIARY,
}

export default function*(feathersApp) {
  try {
    yield all([
      fork(exportEmailControllerSaga, feathersApp),
      fork(exportPrintControllerSaga, feathersApp),
      fork(initEmailTransactionData, feathersApp),
    ]);
  } catch (err) {
    throw err;
  }
}

function* exportEmailControllerSaga(feathersApp) {
  yield takeEvery(
    EXPORT_EMAIL_TRANSACTION,
    callExportEmailControllerSaga,
    feathersApp,
  );
}

function* callExportEmailControllerSaga(feathersApp, action) {
  try {
    const { docType, transactionId, message, recipient, subject } = action.payload;
    const { data, fileName } = yield call(getPdf, feathersApp, {
      docType,
      id: transactionId,
      printType: 'email_attachment',
    });

    const emailParams = {
      subject,
      to: recipient,
      text: message,
      attachments: [
        {
          content: data,
          contentType: 'application/pdf',
          encoding: 'base64',
          filename: fileName,
        },
      ],
    };

    yield call(email, feathersApp, emailParams);
  } catch (err) {
    console.error('@callExportEmailControllerSaga::error', err);
  }
}

function* exportPrintControllerSaga(feathersApp) {
  yield takeEvery(
    EXPORT_PRINT_TRANSACTION,
    callExportPrintControllerSage,
    feathersApp,
  );
}

function* callExportPrintControllerSage(feathersApp, action) {
  try {
    const { payload } = action;
    const { id, docType } = payload;
    const { data, fileName } = yield call(getPdf, feathersApp, {
      id,
      docType,
    });

    const downloadPayload = {
      data,
      download: true,
      encoding: 'base64',
      format: 'pdf',
      mimeType: 'application/octet-stream',
      name: fileName,
    };

    yield put({ type: IMMEDIATE_DOWNLOAD, payload: downloadPayload });
  } catch (err) {
    console.error('@printExport::error', err);
  }
}

function* initEmailTransactionData(feathersApp) {
  yield takeEvery(
    'EMAIL_TRANSACTION_MODAL_INITIALIZE',
    callInitEmailTransactionData,
    feathersApp
  );
}

function* callInitEmailTransactionData(feathersApp, action) {
  yield put(showEmailTransactionModal());
  yield put(setEmailTransactionModalLoading(true));

  const { payload } = action;
  const { id, docType } = payload;

  try {
    const document = yield call(getDoc, feathersApp, {
      id,
      docType,
    });

    const { subsidiary_id, location_id } = document;

    const templateId = yield call(getPrintTemplateId, {
      docType,
      type: 'email',
      locationId: location_id,
      subsidiaryId: subsidiary_id,
    });

    if (!templateId) {
      throw new Error(`Template preference not found for email (${docType})`);
    }

    const printTemplate = yield call(
      fetchPrintTemplateById,
      feathersApp,
      { id: templateId },
    );

    if (!printTemplate) {
      throw new Error(`Template not found for email (${docType})`);
    }

    const { template_text, template_text_subject } = printTemplate;

    const templateData = yield call(
      buildTransactionTemplate,
      feathersApp,
      document,
    );

    const messageCompiler = Handlebars.compile(template_text);
    const message = messageCompiler(templateData);

    const subjectCompiler = Handlebars.compile(template_text_subject);
    const subject = subjectCompiler(templateData);

    const recipient = ((templateData || {}).customer || {}).email;

    yield put(setEmailTransactionModalData({
      docType,
      message,
      recipient,
      subject,
      transactionId: id,
    }));
  } catch (err) {
    console.error(
      '@saga_export_transaction::callInitEmailTransactionData::error',
      err
    );

    yield put(setEmailTransactionModalData({
      docType,
      transactionId: id,
    }));
  } finally {
    yield put(setEmailTransactionModalLoading(false));
  }
}

export function* buildTransactionTemplate(feathersApp, data = {}) {
  const { customer_id, subsidiary_id, location_id } = data;

  const { value: timeFormat } = yield select(
    getParticularPreferenceSettings,
    'time_format',
    ['value'],
    location_id,
    subsidiary_id,
  );

  const { value: dateFormat } = yield select(
    getParticularPreferenceSettings,
    'date_format',
    ['value'],
    location_id,
    subsidiary_id,
  );

  const { value: dateTimeFormat } = yield select(
    getParticularPreferenceSettings,
    'date_time_format',
    ['value'],
    location_id,
    subsidiary_id,
  );

  const isAdmin = yield select(isUserAdmin);
  const userId = yield select(getCurrentUserId);
  const isOwner = userId === customer_id;

  const transaction = {
    transaction: data,
    subsidiary: data.subsidiary,
    customer: data.customer,
    location: data.location,
    meta: {
      timeFormat,
      dateFormat,
      dateTimeFormat,
    },
  };

  const getDependencyDoc = async (path, docType, id) => {
    const dependencyData = await getDoc(feathersApp, {
      id,
      docType,
    });

    if (Boolean(dependencyData.private_contact_details) && !(isAdmin || isOwner)) {
      dependencyData.address_details = null;
      dependencyData.contact_number_values = null;
      dependencyData.contact_numbers = null;
      dependencyData.contacts = null;
      dependencyData.email = null;
    }

    const { address_details, contacts, contact_numbers } = dependencyData;

    if (address_details) {
      dependencyData.address = getAddress(address_details);
    }

    if (contacts) {
      dependencyData.phone_number = getContactPhoneNumber(contacts);
    }

    if (contact_numbers) {
      dependencyData.phone = getPhoneNumber(contact_numbers);
    }

    set(transaction, path, dependencyData);
  };

  const dependencyPaths = [
    { path: 'customer', id: customer_id },
    { path: 'location', id: location_id },
    { path: 'subsidiary', id: subsidiary_id },
  ];

  yield all(dependencyPaths.map(depPath => {
    const { path, id } = depPath;

    if (!id) return null;

    const docType = DEPENDENCY_PATH_DOCTYPE_MAP[path];
    return getDependencyDoc(path, docType, id);
  }));

  switch (data.doc_type) {
    case DOCTYPE_SALE_TRANSACTION: {
      const itemLines = data.item_lines;
      const hasItemLines = Boolean(itemLines.length);

      if (hasItemLines) {
        const modifierTypes = [
          ITEM_TYPE_PROMOTION,
          ITEM_TYPE_DISCOUNT,
          ITEM_TYPE_COUPON,
          ITEM_TYPE_GIFTCERTIFICATE,
          ITEM_TYPE_GRATUITY,
          ITEM_TYPE_PAYMENT_SURCHARGE,
          ITEM_TYPE_LOYALTY,
        ];

        const giftCertReferences = [];
        let qtyTotal = 0;

        itemLines.forEach(line => {
          const { item_type, reference_id } = line || {};
          const isModifier = modifierTypes.includes(item_type);

          if (!isModifier) {
            const qty = Number(line.uom_quantity || line.quantity || 0);
            qtyTotal += qty;
          }

          if (item_type === ITEM_TYPE_GIFTCERTIFICATE && reference_id) {
            giftCertReferences.push(reference_id);
          }
        });

        transaction.meta.quantity = qtyTotal;

        if (giftCertReferences.length) {
          const creditCertificates = yield call(getDocs, feathersApp, {
            list_type: 'credit_certificate',
            filters: {
              certificate_ids: giftCertReferences,
              rows_per_page: giftCertReferences.length,
            },
          });

          transaction.meta.credit_certificates = creditCertificates.data;
        }
      }

      break;
    }

    default:
      break;
  }

  return transaction;
}

function getAddress(addressDetails) {
  if (!addressDetails || addressDetails.length <= 0) return '';
  const address = (addressDetails.find((a) => a.preferred) || {}).address1;
  return address;
}

function getContactPhoneNumber(contacts) {
  if (!contacts || contacts.length <= 0) return '';
  const contactPhoneNumber = (contacts.find((c) => c.primary_contact) || {})
    .contact_number;
  return contactPhoneNumber;
}

function getPhoneNumber(numbers) {
  if (!numbers || numbers.length <= 0) return '';
  const phoneNumber = (numbers.find((n) => n.primary) || {}).value;
  return phoneNumber;
}

export function* getPrintTemplateId(params) {
  try {
    const { docType, type, locationId, subsidiaryId } = params;
    const setting = (EXPORT_PREFERENCE_MAP[docType] || {})[type];

    if (!setting) return;

    const { value: templateId } = yield select(
      getParticularPreferenceSettings,
      setting,
      ['value'],
      locationId,
      subsidiaryId,
    );

    return templateId;
  } catch {
    return;
  }
}

function* getPdf(feathersApp, params) {
  const { id, docType, printType } = params;

  if (!id) {
    throw new Error('Document ID not found.');
  }

  const document = yield call(getDoc, feathersApp, {
    id,
    docType,
  });

  const { subsidiary_id, location_id, reference_id } = document;

  const templateId = yield call(getPrintTemplateId, {
    docType,
    type: printType || 'print',
    locationId: location_id,
    subsidiaryId: subsidiary_id,
  });

  if (!templateId) {
    throw new Error(`Template preference not found for print (${docType})`);
  }

  const templateData = yield call(
    buildTransactionTemplate,
    feathersApp,
    document,
  );

  const { data } = yield call(generatePdf, feathersApp, {
    templateId,
    transaction: templateData,
    parser: 'handlebar'
  });

  const prefix = docType.replace('_', ' ').toUpperCase();
  const fileName = `${prefix} - ${reference_id}`;

  return { data, fileName };
}